import React, { useEffect, useMemo, useState, useRef } from "react";
import { Form, SimpleItem } from "devextreme-react/ui/form";
import { getClientOrgs, userApi } from "../../../api";
import CollapseSection from "../../../components/CollaspeSection";
import { getDateFormat } from "../../../utils/date-format";
import { useQuery } from "@tanstack/react-query";

import { Template } from "devextreme-react/core/template";
import UserSelectBoxItem from "../../../components/SelectBoxItems/UserSelectBoxItem";
import CaseTypesApi from "@/api/case-types";
import CaseStatusApi from "@/api/case-status";
import EvidenceTypesApi from "@/api/evidence-types";
import EvidenceAPI from "@/api/evidence";
import {
  applyTimeIntervalSelection,
  cloneReportInfoForForm,
  getTimeIntervalVisibility,
} from "./report-params-utils";
import {
  ALL_SELECTED_FILTERS_KEY,
  UNASSIGNED_FILTER_VALUE,
  displayWithUnassigned,
  unassignedOption,
  unassignedUserOption,
} from "./filter-constants";

const TIME_DIMENSION_ITEMS = ["Created On", "Intake Date", "Date Released"];

const EvidenceParamsForm = ({ paramsForm, reportInfo }) => {
  const [editorVisiblity, setEditorVisbility] = useState({
    ...getTimeIntervalVisibility(reportInfo?.report_parameters?.time_interval),
    case_status: true,
  });

  const { data: evidenceTypes } = useQuery({
    queryKey: ["evidence:types"],
    queryFn: () => EvidenceTypesApi.getEvidenceTypes(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const { data: evidenceBrands } = useQuery({
    queryKey: ["evidence:brands"],
    queryFn: () => EvidenceAPI.getEvidenceBrands(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const { data: clientOrgs } = useQuery({
    queryKey: ["clients:orgs"],
    queryFn: () => getClientOrgs(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const { data: caseLeads } = useQuery({
    queryKey: ["users", { includeObservers: false }],
    queryFn: () => userApi.getUsers({ includeObservers: false }),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const { data: caseStatuses } = useQuery({
    queryKey: ["caseStatuses"],
    queryFn: () => CaseStatusApi.getCaseStatuses(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const { data: caseTypes } = useQuery({
    queryKey: ["caseTypes"],
    queryFn: () => CaseTypesApi.getCaseTypes(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  // Option lists are memoized so the seeding effects below can set tag box
  // values with the same object identities the editors' items use (required
  // for the user object tag box, which has no valueExpr).
  const organizationOptions = useMemo(
    () => [unassignedOption("organization"), ...clientOrgs],
    [clientOrgs]
  );

  const evidenceTypeOptions = useMemo(
    () => [unassignedOption("evi_type"), ...evidenceTypes],
    [evidenceTypes]
  );

  const evidenceBrandOptions = useMemo(
    () => [unassignedOption("manufacturer"), ...evidenceBrands],
    [evidenceBrands]
  );

  const caseTypeOptions = useMemo(
    () => [unassignedOption("case_type"), ...caseTypes],
    [caseTypes]
  );

  const caseLeadOptions = useMemo(
    () => [
      unassignedUserOption("(No Case Lead)"),
      ...caseLeads.map((cl) => ({
        user_id: cl.user_id,
        email: cl.email,
        full_name: cl.full_name,
        title: cl.title,
      })),
    ],
    [caseLeads]
  );

  // Fields saved as "all selected" reseed their tag box fully checked once
  // the lookup resolves. Values are derived from the current options, not a
  // snapshot, so options added since the save show up checked too.
  const allSelectedFilters = useMemo(
    () => reportInfo?.report_parameters?.[ALL_SELECTED_FILTERS_KEY] || [],
    [reportInfo]
  );

  const seededRef = useRef({});
  const seedAllSelected = (paramKey, values) => {
    if (seededRef.current[paramKey]) return;
    if (!allSelectedFilters.includes(paramKey)) return;
    // A sentinel bearing field carries its "__unassigned__" option
    // synchronously, before the lookup resolves, so wait for at least one real
    // (non sentinel) value before seeding. Seeding and locking on the sentinel
    // only state would strand the field on "(No ...)".
    const hasRealValue = values.some((v) =>
      v && typeof v === "object"
        ? !Object.values(v).includes(UNASSIGNED_FILTER_VALUE)
        : v !== UNASSIGNED_FILTER_VALUE
    );
    if (!hasRealValue) return;
    // Seed each all selected field once. Re-running on later option identity
    // changes (e.g. a shared query refetch) would reset the tag box to fully
    // checked and clobber a partial deselection the user made mid edit.
    seededRef.current[paramKey] = true;
    paramsForm.current?.instance?.updateData(
      `report_parameters.${paramKey}`,
      values
    );
  };

  useEffect(() => {
    seedAllSelected(
      "organization",
      organizationOptions.map((o) => o.organization)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "evidence_types",
      evidenceTypeOptions.map((o) => o.evi_type)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceTypeOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "evidence_brands",
      evidenceBrandOptions.map((o) => o.manufacturer)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceBrandOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected("case_lead", caseLeadOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseLeadOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "case_statuses",
      caseStatuses.map((s) => s.status_name)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseStatuses, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "case_types",
      caseTypeOptions.map((t) => t.case_type)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseTypeOptions, allSelectedFilters]);

  let startDateRef = null;
  let endDateRef = null;

  return (
    <>
      <CollapseSection
        title={"Report Parameters | Evidence - Overview"}
        allowCollapse={false}
      >
        <Form
          ref={paramsForm}
          colCount={2}
          defaultFormData={cloneReportInfoForForm(reportInfo)}
        >
          <SimpleItem
            dataField="report_parameters.time_interval"
            label={{ text: "Time Interval", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            editorOptions={{
              items: [
                "All Time",
                "Custom",
                "Today",
                "Yesterday",
                "This Week",
                "Last Week",
                "This Month",
                "Last Month",
                "This Year",
                "Last Year",
              ],
              placeholder: "All Time",
              dropDownOptions: {
                maxHeight: 250,
              },
              onValueChanged: (e) => {
                const interval = applyTimeIntervalSelection(
                  e,
                  paramsForm.current?.instance,
                  TIME_DIMENSION_ITEMS
                );
                setEditorVisbility({
                  ...editorVisiblity,
                  ...getTimeIntervalVisibility(interval),
                });
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.timestamp_dimension"
            label={{ text: "Time Dimension", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            isRequired={true}
            visible={editorVisiblity.dimension}
            editorOptions={{
              items: TIME_DIMENSION_ITEMS,
            }}
          />
          <SimpleItem
            dataField="report_parameters.start_date"
            label={{ text: "Start Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.startDate}
            editorOptions={{
              onInitialized: (e) => {
                startDateRef = e.component;
              },
              onValueChanged: (e) => {
                endDateRef.option("min", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.end_date"
            label={{ text: "End Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.endDate}
            editorOptions={{
              onInitialized: (e) => {
                endDateRef = e.component;
              },
              onValueChanged: (e) => {
                startDateRef.option("max", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.organization"
            label={{ text: "Organization" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              displayExpr: displayWithUnassigned(
                "organization",
                "(No Organization)"
              ),
              valueExpr: "organization",
              items: organizationOptions,
              placeholder: "All Organizations",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.evidence_types"
            label={{ text: "Evidence Types" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "evi_type",
              displayExpr: displayWithUnassigned(
                "evi_type",
                "(No Evidence Type)"
              ),
              items: evidenceTypeOptions,
              placeholder: "All Evidence Types",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.evidence_brands"
            label={{ text: "Evidence Providers" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "manufacturer",
              displayExpr: displayWithUnassigned(
                "manufacturer",
                "(No Provider)"
              ),
              items: evidenceBrandOptions,
              placeholder: "All Evidence Providers",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.case_lead"
            label={{ text: "Case Lead" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              displayExpr: "full_name",
              items: caseLeadOptions,
              placeholder: "All Case Leads",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
              itemTemplate: "report_parameters.case_lead",
            }}
          />
          <Template
            name="report_parameters.case_lead"
            render={(data) => <UserSelectBoxItem data={data} />}
          />
          <SimpleItem
            dataField="report_parameters.case_types"
            label={{ text: "Case Types" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "case_type",
              displayExpr: displayWithUnassigned("case_type", "(No Case Type)"),
              items: caseTypeOptions,
              placeholder: "All Case Types",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.case_statuses"
            label={{ text: "Case Status" }}
            colSpan={1}
            editorType="dxTagBox"
            visible={editorVisiblity.case_status}
            editorOptions={{
              valueExpr: "status_name",
              displayExpr: "status_name",
              items: caseStatuses,
              placeholder: "All Case Statuses",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
        </Form>
      </CollapseSection>
    </>
  );
};

export default EvidenceParamsForm;
